/**
 * The Spark example-annoyance library.
 *
 * Seeds the "Nothing coming to mind? Tap an example" affordance on every Spark
 * surface (the /spark page, the homepage Spark modal, and the in-app studio).
 * Tapping a chip fills the vent field with the full `text` sentence (the model
 * needs a real sentence, not a two-word fragment) and focuses it — never
 * auto-submits.
 *
 * CURATION BAR — every entry must pass all three, because these examples ARE
 * the product's pitch for what counts as a Spark-able problem:
 *   1. SHARED — thousands of people plausibly have it ("probably annoying a lot
 *      of other people too"). No niche or personal-circumstance gripes.
 *   2. PRODUCT-SOLVABLE — a gadget, app, service, or material could address it,
 *      so a market read makes sense ("whether there's a business in it").
 *   3. NOT INTERPERSONAL/SITUATIONAL — "my boss micromanages" is a real
 *      annoyance but not a market; excluded.
 *
 * `label` is the short chip text (2-4 words); `text` is the first-person
 * sentence dropped into the field. Grouped by loose theme only for authoring
 * legibility — the runtime picker shuffles across the whole list.
 */
export interface SparkExample {
  label: string;
  text: string;
}

export const SPARK_EXAMPLES: SparkExample[] = [
  // ---- Home & organization ----
  { label: 'Tangled cables', text: 'My charger and earbud cables are always tangled in my bag.' },
  { label: 'Shower drain hair', text: 'My shower drain keeps clogging with hair even with a hair catcher.' },
  { label: 'Missing sock', text: 'Every load of laundry seems to eat one sock and leave the other.' },
  { label: 'Tupperware lids', text: "I can never find the lid that matches the container I'm holding." },
  { label: 'Cluttered cables', text: 'The cables behind my desk are a tangled mess I can never sort out.' },
  { label: 'Fridge leftovers', text: 'I forget about leftovers in the back of the fridge until they go bad.' },
  { label: 'Dead batteries', text: 'I never have the right size battery when a remote or toy dies.' },
  { label: 'Overflowing recycling', text: 'My recycling bin overflows days before collection and I have to stack it.' },
  { label: 'Lost remote', text: 'The TV remote is never where I left it when I sit down to watch.' },
  { label: 'Wobbly table', text: 'One leg of my table is always wobbly and folded napkins never hold.' },

  // ---- Kitchen & food ----
  { label: 'Cold coffee', text: 'My coffee always goes cold before I finish my morning at the desk.' },
  { label: 'Soggy leftovers', text: 'Reheated leftovers come out soggy or unevenly hot in the microwave.' },
  { label: 'Herbs go bad', text: 'Fresh herbs wilt and rot in my fridge before I can use half of them.' },
  { label: 'Meal planning', text: 'I waste time and money because I never know what to cook for the week.' },
  { label: 'Sticky spice jars', text: 'My spice jars get greasy and I can never read the labels while cooking.' },
  { label: 'Cutting board juice', text: 'Juice from vegetables runs off my cutting board all over the counter.' },
  { label: 'Ripening avocados', text: 'My avocados go from rock hard to brown mush with no window in between.' },
  { label: 'Grocery spoilage', text: 'I keep buying groceries that spoil before I get around to using them.' },
  { label: 'Water bottle funk', text: 'My reusable water bottle develops a smell I can never fully scrub out.' },
  { label: 'Overflowing trash', text: 'The kitchen trash bag always overflows and tears when I pull it out.' },

  // ---- Tech & devices ----
  { label: 'Phone at 20%', text: 'My phone battery dies by mid-afternoon and I am always hunting for a charger.' },
  { label: 'Too many passwords', text: 'I can never remember which password goes with which account.' },
  { label: 'Cracked screen', text: 'My phone screen cracks within weeks no matter which case I buy.' },
  { label: 'Slow wifi spots', text: 'The wifi drops in certain rooms of my home and I never know why.' },
  { label: 'Cable clutter', text: 'I have a drawer full of cables and no idea which device they belong to.' },
  { label: 'Lost AirPods', text: 'I lose one earbud constantly and the case is never charged when I need it.' },
  { label: 'Smart home chaos', text: 'Every smart device needs its own app and none of them talk to each other.' },
  { label: 'Photo overload', text: 'I have thousands of phone photos and can never find the one I want.' },
  { label: 'Endless spam calls', text: 'I get so many spam calls I stop answering numbers I do not recognize.' },
  { label: 'Fading screenshots', text: 'I screenshot things to remember them and then never look at them again.' },

  // ---- Commute & car ----
  { label: 'Parking at pickup', text: "I can never find parking near my kid's school at pickup time." },
  { label: 'Forgotten parking spot', text: 'I forget where I parked in big garages and wander for ten minutes.' },
  { label: 'Windshield ice', text: 'Scraping ice off my windshield every winter morning makes me late.' },
  { label: 'Cup holder mess', text: 'My car cup holders collect sticky spills and crumbs I can never clean.' },
  { label: 'Phone mount fails', text: 'My phone mount keeps slipping off the dash while I am driving.' },
  { label: 'Gas station guess', text: 'I never know which side my gas tank is on in a rental car.' },
  { label: 'Toll fumbling', text: 'I fumble for change or a pass at tolls and hold up the line behind me.' },
  { label: 'Grocery bags tip', text: 'My grocery bags tip over in the trunk and spill on the drive home.' },
  { label: 'Backseat boredom', text: 'My kids get bored and restless within minutes on any car trip.' },
  { label: 'Cold car seat', text: 'Getting into a freezing car seat on winter mornings is miserable.' },

  // ---- Health, body & sleep ----
  { label: 'Pill reminders', text: 'I forget whether I already took my daily vitamins or medication.' },
  { label: 'Water intake', text: 'I never drink enough water and only notice when I already have a headache.' },
  { label: 'Bad posture', text: 'I slouch at my desk all day and my neck and back ache by evening.' },
  { label: 'Snooze spiral', text: 'I hit snooze five times and still wake up groggy and rushed.' },
  { label: 'Dry winter skin', text: 'My hands crack and dry out every winter no matter what lotion I use.' },
  { label: 'Earbud workouts', text: 'My earbuds fall out every time I run or work out and I lose my rhythm.' },
  { label: 'Snoring nights', text: 'Snoring keeps me or my partner up and nothing we try seems to help.' },
  { label: 'Sweaty gym bag', text: 'My gym clothes sit damp and smelly in my bag until I get home.' },
  { label: 'Forgotten stretches', text: 'I know I should stretch but I never remember until something hurts.' },
  { label: 'Blue light eyes', text: 'My eyes are strained and tired after a full day staring at screens.' },

  // ---- Kids & family ----
  { label: 'Lost kid shoes', text: 'My kids can never find both shoes when we are already running late.' },
  { label: 'Lunchbox ideas', text: 'I run out of lunchbox ideas my kids will actually eat by mid-week.' },
  { label: 'Toy everywhere', text: 'Toys end up scattered across every room and cleanup never ends.' },
  { label: 'Screen time fights', text: 'Getting my kids off their screens turns into a fight every single day.' },
  { label: 'Homework tracking', text: "I can never keep track of what homework and permission slips are due." },
  { label: 'Outgrown clothes', text: 'My kids outgrow clothes so fast I can never keep their drawers current.' },
  { label: 'Bath time splash', text: 'Bath time soaks the whole bathroom floor and I mop up every night.' },
  { label: 'Car seat straps', text: 'Buckling a squirming toddler into a car seat is a daily wrestling match.' },

  // ---- Pets ----
  { label: 'Pet hair everywhere', text: 'My pet sheds so much that hair covers every surface and my clothes.' },
  { label: 'Muddy dog paws', text: 'My dog tracks mud through the house after every single walk.' },
  { label: 'Overfeeding pet', text: 'I forget whether someone already fed the dog and worry about overfeeding.' },
  { label: 'Litter box smell', text: 'The litter box smell fills the room no matter how often I scoop it.' },
  { label: 'Tangled leash', text: 'My dog wraps the leash around my legs and poles on every walk.' },
  { label: 'Spilled pet water', text: 'My pet knocks over the water bowl and soaks the floor while I am out.' },

  // ---- Clothing & laundry ----
  { label: 'Shirt wrinkles', text: 'My shirts come out of the dryer wrinkled and I never have time to iron.' },
  { label: 'Static cling', text: 'My clothes cling with static all winter and it drives me crazy.' },
  { label: 'Pilled sweaters', text: 'My favorite sweaters pill and look worn out after just a few washes.' },
  { label: 'Shoe scuffs', text: 'My white sneakers scuff and stain within a week of buying them.' },
  { label: 'Deodorant marks', text: 'I get white deodorant streaks on dark shirts as I pull them on.' },
  { label: 'Lint everywhere', text: 'Lint and pet hair cling to my black clothes no matter what I try.' },

  // ---- Travel & outdoors ----
  { label: 'Overpacking', text: 'I always overpack and still forget the one thing I actually needed.' },
  { label: 'Tangled headphones', text: 'My headphones and chargers are a tangled knot in every carry-on.' },
  { label: 'Melting sunscreen', text: 'Sunscreen sweats into my eyes and stings within minutes outdoors.' },
  { label: 'Campsite bugs', text: 'Bugs swarm my campsite the moment the sun goes down every trip.' },
  { label: 'Lost luggage tag', text: 'I never know if my checked bag actually made it onto my flight.' },
  { label: 'Wet umbrella', text: 'A wet umbrella drips all over everything the second I get indoors.' },
  { label: 'Cold hands outside', text: 'My hands go numb outdoors but gloves make my phone useless.' },
  { label: 'Sandy beach bag', text: 'Sand gets into everything in my beach bag and never comes out.' },

  // ---- Work & desk ----
  { label: 'Endless meetings', text: 'My calendar fills with meetings that could have been a two-line message.' },
  { label: 'Note chaos', text: 'My notes are scattered across apps and sticky pads and I lose track.' },
  { label: 'Cold desk lunch', text: 'I forget my lunch at my desk and eat it cold hours after it went bad.' },
  { label: 'Standing desk strain', text: 'I stand too long at my desk and my feet and lower back ache by noon.' },
  { label: 'Receipt piles', text: 'I lose track of receipts I need for expenses and taxes every year.' },
];

/**
 * Pick `count` random examples from the library without repeats. A fresh draw
 * each call, so the affordance feels alive across visits and on shuffle. If a
 * caller ever asks for more than the library holds, it returns the whole list
 * shuffled (never repeats, never throws).
 */
export function pickSparkExamples(count: number): SparkExample[] {
  const pool = [...SPARK_EXAMPLES];
  // Fisher-Yates, partial — only shuffle as far as we need.
  const n = Math.min(count, pool.length);
  for (let i = 0; i < n; i++) {
    const j = i + Math.floor(Math.random() * (pool.length - i));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, n);
}
